/*
 * Sticker Wall — scatter stickers into a loose grid, let visitors drag them
 * around, and click one to open a lightbox with an Add to cart button.
 * Progressive enhancement: without JS the section is a tidy flex scatter and
 * each sticker links straight to its product page.
 */

// Deterministic pseudo-random in [0,1) so the scatter is stable across reloads.
function rnd(seed) {
  const s = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

const clamp = (min, v, max) => Math.max(min, Math.min(max, v));

function layout(board) {
  const stickers = Array.from(board.querySelectorAll('[data-sticker]'));
  if (!stickers.length) return;

  const width = board.clientWidth;
  const ws = stickers.map((s) => s.offsetWidth || 160);
  const hs = stickers.map((s, i) => s.offsetHeight || ws[i]);
  const sample = ws[0];
  const cols = clamp(2, Math.round(width / (sample * 1.15)), 6);
  const rows = Math.ceil(stickers.length / cols);
  const cellW = width / cols;
  const maxH = Math.max.apply(null, hs);
  const cellH = maxH * 0.9 + 16; // slight vertical overlap between rows
  const pad = 21; // inset from the frame so stickers don't crowd the edges
  const boardH = rows * cellH + maxH * 0.3 + pad * 2;

  board.style.height = boardH + 'px';

  stickers.forEach((el, i) => {
    if (el.dataset.placed === 'user') return; // leave stickers the visitor moved
    const col = i % cols;
    const row = Math.floor(i / cols);
    const sw = ws[i];
    const sh = hs[i];
    const jitterX = (rnd(i * 2 + 1) - 0.5) * Math.max(0, cellW - sw) * 0.8;
    const jitterY = (rnd(i * 2 + 2) - 0.5) * cellH * 0.18;
    const x = clamp(pad, col * cellW + (cellW - sw) / 2 + jitterX, width - sw - pad);
    const y = clamp(pad, pad + row * cellH + jitterY, boardH - sh - pad);
    el.style.left = x + 'px';
    el.style.top = y + 'px';
  });
}

// No dragging — that keeps native touch-scrolling working on mobile. A tap
// brings the sticker to the front and opens the preview lightbox.
function attachClick(el, bringToFront, openModal) {
  el.addEventListener('dragstart', (e) => e.preventDefault());
  el.addEventListener('click', (e) => {
    e.preventDefault();
    el.style.zIndex = bringToFront();
    openModal(el);
  });
}

function setupModal(root) {
  const modal = root.querySelector('[data-sw-modal]');
  if (!modal) return () => {};

  const img = modal.querySelector('.sw-modal__img');
  const title = modal.querySelector('.sw-modal__title');
  const price = modal.querySelector('.sw-modal__price');
  const form = modal.querySelector('[data-sw-form]');
  const variant = modal.querySelector('[data-sw-variant]');
  const addBtn = modal.querySelector('[data-sw-add]');
  const details = modal.querySelector('[data-sw-details]');
  const status = modal.querySelector('[data-sw-status]');

  let openToken = 0;

  const close = () => {
    modal.hidden = true;
    document.documentElement.style.overflow = '';
  };

  const open = (el) => {
    const token = ++openToken;
    // Show the clicked sticker's already-loaded thumbnail immediately, then
    // upgrade to the hi-res image once it has loaded — so we never flash the
    // previously-opened sticker while the new image downloads.
    const thumb = el.querySelector('.sticker__img');
    const instant = (thumb && (thumb.currentSrc || thumb.src)) || el.dataset.imageLarge || '';
    const large = el.dataset.imageLarge || '';
    img.src = instant;
    if (large && large !== instant) {
      const pre = new Image();
      pre.onload = () => {
        if (openToken === token) img.src = large;
      };
      pre.src = large;
    }
    img.alt = el.dataset.title || '';
    title.textContent = el.dataset.title || '';
    price.textContent = el.dataset.price || '';
    details.href = el.dataset.url || '#';
    status.textContent = '';
    status.hidden = true;

    const vid = el.dataset.variantId || '';
    const available = el.dataset.available === 'true' && vid;
    variant.value = vid;
    addBtn.disabled = !available;
    addBtn.textContent = available ? 'Add to cart' : 'Sold out';

    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    addBtn.focus();
  };

  modal.querySelectorAll('[data-sw-close]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) close();
  });

  form.addEventListener('submit', async (e) => {
    if (!variant.value) return;
    e.preventDefault();
    const label = addBtn.textContent;
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';
    try {
      const addUrl = form.action.replace(/\/+$/, '') + '.js';
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variant.value, quantity: 1 }),
      });
      if (!res.ok) throw new Error('add failed');
      addBtn.textContent = 'Added ✓';
      status.hidden = false;
      status.innerHTML = '<a href="/cart">View cart →</a>';

      // Update the header cart bubble live by firing the theme's own cart event
      // (cart-icon.js listens for 'cart:update' and reads detail.data.itemCount).
      let cart = null;
      try {
        const cartUrl = form.action.replace(/\/add$/, '') + '.js';
        cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
      } catch (e) {
        /* fall back to optimistic +1 below */
      }
      const evt = new Event('cart:update', { bubbles: true });
      evt.detail = cart
        ? { resource: cart, sourceId: 'sticker-wall', data: { source: 'sticker-wall', itemCount: cart.item_count } }
        : { resource: null, sourceId: 'sticker-wall', data: { source: 'product-form-component', itemCount: 1 } };
      document.dispatchEvent(evt);
    } catch (err) {
      addBtn.disabled = false;
      addBtn.textContent = label;
      form.submit(); // fall back to a normal add-to-cart submit
    }
  });

  return open;
}

// Blur-up: swap each low-res sticker for its hi-res once that has loaded.
function upgradeImages(board) {
  board.querySelectorAll('img.sticker__img[data-hires]').forEach((img) => {
    const hi = img.dataset.hires;
    if (!hi) {
      img.classList.add('is-loaded');
      return;
    }
    const loader = new Image();
    loader.onload = () => {
      img.src = hi;
      img.classList.add('is-loaded');
    };
    loader.onerror = () => img.classList.add('is-loaded');
    loader.src = hi;
  });
}

// Swap the tiled cork from its low-res placeholder to the full image once loaded.
function upgradeCork(board) {
  const hi = board.dataset.corkHires;
  if (!hi) return;
  const loader = new Image();
  loader.onload = () => board.style.setProperty('--cork-image', `url(${hi})`);
  loader.src = hi;
}

function init(board) {
  if (board.dataset.ready) return;
  board.dataset.ready = '1';
  board.classList.add('is-interactive');

  upgradeImages(board);
  upgradeCork(board);

  const root = board.closest('.sticker-wall') || document;
  const openModal = setupModal(root);

  layout(board);

  let z = 10;
  const bringToFront = () => (z += 1);
  board.querySelectorAll('[data-sticker]').forEach((el) => attachClick(el, bringToFront, openModal));

  window.addEventListener('load', () => layout(board));
  let t;
  window.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(() => layout(board), 200);
  });
}

function boot() {
  document.querySelectorAll('[data-sticker-wall]').forEach(init);
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);

document.addEventListener('shopify:section:load', boot);
